import type { PatternFormField, PatternFormFieldItem } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { isNotNull, isType } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type { Pattern } from '~/common/model/api/batch-type/pattern';
import type {
  RoutingDocuwareCabinetField,
  RoutingSettingsDocuware,
  RoutingSettingsDocuwareCabinet,
  RoutingSettingsDocuwareInbox,
} from '~/common/model/api/batch-type/process-flow';
import type { IntegrationDocuwareField, IntegrationDocuwareLocationCabinet, IntegrationDocuwareLocationInbox } from '~/common/model/api/integration';

import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

import type { ProcessFlowStepRoutingDocuwareFieldsFormField } from './_routing-docuware.type';

const logger = createLogger('logic/feature/settings/batch-type-library');

// * Transformation to text is not necessary
const IS_TRANSFORMATION_TEXT = false;

/** Type guard for RoutingSettingsDocuwareInbox. */
export const isRoutingSettingsDocuware = {
  inbox: (settings: RoutingSettingsDocuware): settings is RoutingSettingsDocuwareInbox =>
    isType<IntegrationDocuwareLocationInbox>(settings.destination.location, 'inbox'),

  cabinet: (settings: RoutingSettingsDocuware): settings is RoutingSettingsDocuwareCabinet =>
    isType<IntegrationDocuwareLocationCabinet>(settings.destination.location, 'cabinet'),
};

/**
 * Create FieldPattern from FieldPattern FormField.
 *
 * @param {PatternFormField} formField FieldPattern FormField.
 *
 * @returns {Pattern[]} FieldPattern.
 */
export function createFieldPattern(formField: PatternFormField): Pattern[] {
  const scope: PatternAccessScope = 'document';

  return formField.reduce<Pattern[]>((prev, item) => {
    let pattern: Nullable<Pattern>;
    if (typeof item === 'string') {
      pattern = patternLogic.createPattern.fromText(item);
    } else {
      switch (item.tabKey) {
        case 'systemValue': {
          pattern = patternLogic.createPattern.fromSystemValue(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'globalVariable': {
          pattern = patternLogic.createPattern.fromGlobalVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchField': {
          pattern = patternLogic.createPattern.fromBatchField(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchVariable': {
          pattern = patternLogic.createPattern.fromBatchVariable(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchBusinessRule': {
          pattern = patternLogic.createPattern.fromBusinessRule(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentField': {
          pattern = patternLogic.createPattern.fromDocumentField(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentVariable': {
          pattern = patternLogic.createPattern.fromDocumentVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentBusinessRule': {
          pattern = patternLogic.createPattern.fromDocumentBusinessRule(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'parameter': {
          pattern = patternLogic.createPattern.fromParameter(item.value as string);
          break;
        }

        case 'output': {
          pattern = patternLogic.createPattern.fromOutput(item.value as string);
          break;
        }

        default: {
          pattern = undefined;
          break;
        }
      }
    }

    if (isNotNull(pattern)) {
      prev.push(pattern);
    } else {
      logger.writeWithData('warn', 'Invalid pattern item', { cause: item });
    }

    return prev;
  }, []);
}

/**
 * Create FieldPattern FormField from FieldPattern.
 *
 * @param {Pattern[]} fieldPattern FieldPattern.
 *
 * @returns {PatternFormField} FieldPattern FormField.
 */
export function createFieldPatternFormField(fieldPattern: Pattern[]): PatternFormField {
  return fieldPattern.reduce<PatternFormField>((prev, pattern) => {
    let item: Nullable<PatternFormFieldItem>;
    switch (pattern.$is) {
      case 'const': {
        item = patternLogic.createPatternFormFieldItem.fromPatternConstant(pattern);
        break;
      }

      case 'now':
      case 'guid':
      case 'random': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGenerate(pattern, IS_TRANSFORMATION_TEXT);
        break;
      }

      case 'global': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGlobal(pattern);
        break;
      }

      case 'batch': {
        item = patternLogic.createPatternFormFieldItem.fromPatternBatch(pattern);
        break;
      }

      case 'document': {
        item = patternLogic.createPatternFormFieldItem.fromPatternDocument(pattern);
        break;
      }

      case 'parameter': {
        item = patternLogic.createPatternFormFieldItem.fromPatternParameter(pattern);
        break;
      }

      case 'output': {
        item = patternLogic.createPatternFormFieldItem.fromPatternOutput(pattern);
        break;
      }

      default: {
        item = undefined;
        break;
      }
    }

    if (isNotNull(item)) {
      prev.push(item);
    } else {
      logger.writeWithData('warn', 'Invalid pattern', { cause: pattern });
    }

    return prev;
  }, []);
}

/**
 * Create ProcessFlow Step Routing Docuware Fields form Docuware Fields FormField.
 *
 * @param {IntegrationDocuwareField[]} fields Docuware Fields.
 * @param {ProcessFlowStepRoutingDocuwareFieldsFormField} fieldsFormField Docuware Fields FormField.
 *
 * @returns {RoutingDocuwareCabinetField[]} Docuware Fields.
 */
export function createRoutingDocuwareFields(
  fields: IntegrationDocuwareField[],
  fieldsFormField: ProcessFlowStepRoutingDocuwareFieldsFormField,
): RoutingDocuwareCabinetField[] {
  return fields.map((field) => {
    const value = fieldsFormField[field.name];
    if (!value) {
      return {
        $is: field.type,
        name: field.name,
        value: [],
      } as RoutingDocuwareCabinetField;
    }

    return {
      $is: field.type,
      name: field.name,
      value: createFieldPattern(value),
    };
  });
}

/**
 * Create Docuware Fields FormField from Docuware Fields.
 *
 * @param {RoutingDocuwareCabinetField[]} fields Docuware Fields.
 *
 * @returns {ProcessFlowStepRoutingDocuwareFieldsFormField} Docuware Fields FormField.
 */
export function createRoutingDocuwareFieldsFormField(fields: RoutingDocuwareCabinetField[]): ProcessFlowStepRoutingDocuwareFieldsFormField {
  return fields.reduce<ProcessFlowStepRoutingDocuwareFieldsFormField>((prev, field) => {
    prev[field.name] = createFieldPatternFormField(field.value);

    return prev;
  }, {});
}

/**
 * Create Routing Docuware Field default value.
 *
 * @param {IntegrationDocuwareField} field Docuware Field.
 *
 * @returns {PatternFormField} Routing Docuware Field default value.
 */
export function createRoutingDocuwareFieldDefaultValue(field?: IntegrationDocuwareField): PatternFormField {
  if (!field || field.type === 'table' || !('defaultValue' in field) || !field.defaultValue?.length) {
    return [];
  }

  return Array.isArray(field.defaultValue) ? createFieldPatternFormField(field.defaultValue as Pattern[]) : createFieldPatternFormField([field.defaultValue]);
}

/**
 * Create Routing Docuware Fields initial value.
 *
 * @param {IntegrationDocuwareField[]} fields Docuware Fields.
 *
 * @returns {ProcessFlowStepRoutingDocuwareFieldsFormField} Routing Docuware Fields initial value.
 */
export function createRoutingDocuwareFieldsInitialValue(fields?: IntegrationDocuwareField[]): Nullable<ProcessFlowStepRoutingDocuwareFieldsFormField> {
  if (!fields?.length) {
    return {};
  }

  return fields.reduce<ProcessFlowStepRoutingDocuwareFieldsFormField>((prev, field) => {
    if (field.type === 'table') {
      prev[field.name] = [];
    } else {
      prev[field.name] = createRoutingDocuwareFieldDefaultValue(field);
    }

    return prev;
  }, {});
}
